import { useCallback, useMemo, useState } from 'react';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { useCalculationWorkflow } from '@dealstack/frontend/app/pages/Valuations/queries/useCalculationWorkflow';
import { useBreakpointSummaryQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useBreakpointSummaryQuery';
import { usePersonSelection } from '@dealstack/frontend/app/pages/Valuations/hooks/usePersonSelection';
import { useValuationBreadcrumbs } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationBreadcrumbs';
import { ValuationLocation } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import type { SecurityComponentInfo } from '@dealstack/frontend/app/pages/Valuations/shared/securityComponentInfo.types';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';

export function useBreakpointSummaryView() {
	const { pathValuationId, dealId } = useValuationNavigation();
	const { valuations } = useValuationsQuery(dealId);
	const currentValuation = useMemo(
		() => valuations.find((v) => v.id === pathValuationId),
		[valuations, pathValuationId]
	);

	const {
		isSummaryFirstLoad,
		summaryByPerson: summaryByPersonData,
		resultId,
		linearSolve,
		reloadSummary,
	} = useCalculationWorkflow(dealId, pathValuationId);

	const {
		selectedPersonId,
		selectedMipId,
		selectedEntity,
		personOptions,
		availableTags,
		loadPerson,
		selectedEntityName,
		pathPersonId,
		pathMipId,
	} = usePersonSelection({
		summaryByPersonData,
		view: ValuationLocation.BREAKPOINT_SUMMARY,
		isSummaryFirstLoad,
	});

	const {
		breakpointSummaryData,
		isBreakpointSummaryFirstLoad,
		isBreakpointSummaryLoading,
		securityComponentMap,
		createOverride,
		updateOverride,
		deleteOverride,
	} = useBreakpointSummaryQuery(
		dealId,
		pathValuationId,
		selectedPersonId,
		selectedMipId,
		resultId,
		reloadSummary
	);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [selectedComponent, setSelectedComponent] = useState<SecurityComponentInfo | null>(null);

	const selectedComponentInfo = useMemo(() => {
		if (!selectedComponent) {
			return null;
		}
		return (
			securityComponentMap.get(
				`${selectedComponent.securityId}-${selectedComponent.component}-${selectedComponent.trancheKey}`
			) ?? null
		);
	}, [selectedComponent, securityComponentMap]);

	const handleOpenOverrideDrawer = useCallback((info: SecurityComponentInfo) => {
		setSelectedComponent(info);
		setIsDrawerOpen(true);
	}, []);

	const handleCloseOverrideDrawer = useCallback(() => {
		setIsDrawerOpen(false);
		setSelectedComponent(null);
	}, []);

	const handleSaveOverride = useCallback(
		async (saveFn: () => Promise<void>) => {
			await saveFn();
			handleCloseOverrideDrawer();
		},
		[handleCloseOverrideDrawer]
	);

	const currency = currentValuation?.defaultCurrency ?? CurrencyType.EUR;

	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	useValuationBreadcrumbs(
		{
			view: 'breakpoint-summary',
			dealId: dealId ?? '',
			valuation: currentValuation ?? undefined,
			personId: pathPersonId,
			mipId: pathMipId,
			selectedEntityName,
		},
		inPageFeatureFlag
	);

	return {
		isSummaryFirstLoad,
		isBreakpointSummaryFirstLoad,
		isBreakpointSummaryLoading,
		breakpointSummaryData,
		currency,
		currentValuation,
		linearSolve,
		selectedPersonId,
		selectedMipId,
		selectedEntity,
		personOptions,
		availableTags,
		loadPerson,
		isDrawerOpen,
		selectedComponentInfo,
		handleOpenOverrideDrawer,
		handleCloseOverrideDrawer,
		handleSaveOverride,
		createOverride,
		updateOverride,
		deleteOverride,
		distributionRuleScope: {
			dealId,
			valuationId: pathValuationId,
			personId: selectedPersonId,
			mipId: selectedMipId,
			resultId,
		},
	};
}
